/**
 * Dispatches an item to the corresponding handler based on its `kind` discriminant.
 * Handlers are checked exhaustively at compile time via the mapped type constraint.
 *
 * Overload 1 — for types that carry `kind` as a required field (e.g. TSegmentPresence):
 *   dispatchByKind(handlers, item)
 *
 * Overload 2 — for types without a runtime `kind` field (e.g. TClipPresence from the API).
 *   A `getKind` extractor derives the discriminant value from the item's shape:
 *   dispatchByKind(handlers, item, getKind)
 */
export function dispatchByKind<T extends { kind: string }>(
  handlers: { [K in T['kind']]: (item: Extract<T, { kind: K }>) => void },
  item: T
): void;
export function dispatchByKind<T, K extends string>(
  handlers: { [P in K]: (item: T) => void },
  item: T,
  getKind: (item: T) => K
): void;
export function dispatchByKind(
  handlers: Record<string, (item: unknown) => void>,
  item: unknown,
  getKind?: (item: unknown) => string
): void {
  const kind = getKind ? getKind(item) : (item as { kind: string }).kind;
  const handler = handlers[kind];
  if (typeof handler !== 'function') {
    throw new Error(
      `dispatchByKind: no handler registered for kind "${kind}". Available kinds: [${Object.keys(handlers).join(', ')}]`
    );
  }
  handler(item);
}

export function removeDeepEmpty(obj: any) {
  for (const key in obj) {
    if (!obj[key]) {
      delete obj[key];
    } else if (typeof obj[key] === 'object') {
      removeDeepEmpty(obj[key]);
    }
  }

  return obj;
}

/**
 * Recursively replaces all empty string values in an object or array with null,
 * but ignores instances of File.
 */
export function emptyStringValues(obj: any): any {
  if (obj instanceof File) return obj;

  if (Array.isArray(obj)) {
    return obj.map(emptyStringValues);
  }

  if (obj && typeof obj === 'object') {
    Object.keys(obj).forEach(key => {
      if (obj[key] === '') {
        obj[key] = null;
      } else if (typeof obj[key] === 'object') {
        obj[key] = emptyStringValues(obj[key]);
      }
    });

    return obj;
  }

  return obj;
}
